import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Title, usePermissions } from 'react-admin';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import GppBadIcon from '@mui/icons-material/GppBad';
import GppGoodIcon from '@mui/icons-material/GppGood';
import { ButtonField, Tag } from '@wk/components-v3-react16';
import { JumpstartButton, JumpstartFilterText, JumpstartFilterSelect } from '../components/JumpstartInputs';
import { API_URL } from '../config';
import { useT } from '../i18n/useT';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FingerprintRecord {
    id: number;
    queue_id: string;
    consumer_email: string;
    consumer_name: string;
    consumer_surname: string;
    sku: string;
    document_type: string;
    payload_hash: string | null;
    encoding_method: string;
    verification_status: string;
    embedded_at: string | null;
}

interface VerifyResult {
    is_verified: boolean;
    confidence: number;
    queue_match?: boolean;
    email_match?: boolean;
    reason?: string;
    extracted_queue_id?: string;
    extracted_email?: string;
    meta_verified?: boolean;
    content_verified?: boolean;
    found_fingerprint?: boolean;
    bits_extracted?: number;
    document_type?: string;
    verification_method?: string;
}

interface ExtractResult {
    status: string;
    document_type?: string;
    queue_id?: string;
    consumer_email?: string;
    bits_extracted?: number;
    verification_method?: string;
    decoded_string?: string;
    payload_parts?: {
        queue_id?: string;
        consumer_email?: string;
        consumer_name?: string;
        consumer_surname?: string;
        sku?: string;
    };
}

interface TamperResult {
    status: string;
    tampering_detected: boolean | null;
    indicators: string[];
    document_type?: string;
}

interface DbMatchResult {
    status: string;
    extraction: ExtractResult;
    tampering: TamperResult;
    matches_found: number;
    matches: Array<{
        fingerprint_id: number;
        queue_id: string;
        consumer_email: string;
        consumer_name: string;
        consumer_surname: string;
        sku: string;
        document_type: string;
        is_match: boolean;
        queue_id_match: boolean;
        email_match: boolean;
    }>;
    message?: string;
}

type VerifyMode = 'manual' | 'db_specific' | 'db_all';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    const tokenType = localStorage.getItem('tokenType') || 'bearer';
    if (!token) return {};
    return { Authorization: `${tokenType} ${token}` };
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('cs-CZ');
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FingerprintVerifyPage = () => {
    const t = useT();
    const { permissions } = usePermissions();

    // File upload
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [documentType, setDocumentType] = useState<'pdf' | 'epub'>('pdf');

    // Mode selector
    const [mode, setMode] = useState<VerifyMode>('manual');

    // Manual mode fields
    const [manualQueueId, setManualQueueId] = useState('');
    const [manualEmail, setManualEmail] = useState('');

    // DB mode
    const [dbFingerprints, setDbFingerprints] = useState<FingerprintRecord[]>([]);
    const [selectedFpId, setSelectedFpId] = useState<number | ''>('');
    const [loadingFps, setLoadingFps] = useState(false);

    // Results
    const [loading, setLoading] = useState(false);
    const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
    const [extractResult, setExtractResult] = useState<ExtractResult | null>(null);
    const [tamperResult, setTamperResult] = useState<TamperResult | null>(null);
    const [dbMatchResult, setDbMatchResult] = useState<DbMatchResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Load fingerprints from DB when switching to DB mode
    useEffect(() => {
        if (mode === 'db_specific' || mode === 'db_all') {
            loadFingerprints();
        }
    }, [mode]);

    const loadFingerprints = useCallback(async () => {
        setLoadingFps(true);
        try {
            const resp = await fetch(`${API_URL}/fingerprint/list?limit=500`, {
                headers: getAuthHeaders(),
            });
            if (resp.ok) {
                const data = await resp.json();
                setDbFingerprints(data.fingerprints || []);
            }
        } catch {
            // ignore
        } finally {
            setLoadingFps(false);
        }
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        if (f) {
            const ext = f.name.split('.').pop()?.toLowerCase();
            if (ext === 'epub') setDocumentType('epub');
            else setDocumentType('pdf');
        }
        // Clear previous results
        clearResults();
    };

    const clearResults = () => {
        setVerifyResult(null);
        setExtractResult(null);
        setTamperResult(null);
        setDbMatchResult(null);
        setError(null);
    };

    const handleVerify = async () => {
        if (!file) return;
        setLoading(true);
        clearResults();

        try {
            if (mode === 'manual') {
                await runManualVerify();
            } else if (mode === 'db_all') {
                await runDbMatch(null);
            } else if (mode === 'db_specific') {
                if (selectedFpId === '') return;
                await runDbMatch(selectedFpId as number);
            }
        } catch (err: any) {
            setError(err.message || t('fingerprintVerify.errors.unexpected'));
        } finally {
            setLoading(false);
        }
    };

    const runManualVerify = async () => {
        if (!file) return;
        const headers = getAuthHeaders();

        // 1. Verify
        const verifyForm = new FormData();
        verifyForm.append('file', file);
        const verifyParams = new URLSearchParams({ document_type: documentType });
        if (manualQueueId) verifyParams.set('expected_queue_id', manualQueueId);
        if (manualEmail) verifyParams.set('expected_consumer_email', manualEmail);

        const verifyResp = await fetch(
            `${API_URL}/fingerprint/verify/document?${verifyParams}`,
            { method: 'POST', headers, body: verifyForm },
        );
        if (!verifyResp.ok) throw new Error(`Verify failed: ${verifyResp.status}`);
        const vr = await verifyResp.json();
        setVerifyResult(vr);

        // 2. Extract
        const extractForm = new FormData();
        extractForm.append('file', file);
        const extractResp = await fetch(
            `${API_URL}/fingerprint/extract?document_type=${documentType}`,
            { method: 'POST', headers, body: extractForm },
        );
        if (extractResp.ok) {
            setExtractResult(await extractResp.json());
        }

        // 3. Tamper check (only if we have a queue_id from extraction or manual input)
        const qid = manualQueueId || vr?.extracted_queue_id;
        if (qid) {
            const tamperForm = new FormData();
            tamperForm.append('file', file);
            const tamperResp = await fetch(
                `${API_URL}/fingerprint/check-tampering?queue_id=${encodeURIComponent(qid)}&document_type=${documentType}`,
                { method: 'POST', headers, body: tamperForm },
            );
            if (tamperResp.ok) {
                setTamperResult(await tamperResp.json());
            }
        }
    };

    const runDbMatch = async (fpId: number | null) => {
        if (!file) return;
        const headers = getAuthHeaders();

        const form = new FormData();
        form.append('file', file);
        const params = new URLSearchParams({ document_type: documentType });
        if (fpId !== null) params.set('fingerprint_id', String(fpId));

        const resp = await fetch(
            `${API_URL}/fingerprint/verify/against-db?${params}`,
            { method: 'POST', headers, body: form },
        );
        if (!resp.ok) throw new Error(`DB match failed: ${resp.status}`);
        const result: DbMatchResult = await resp.json();
        setDbMatchResult(result);

        // Set extraction and tampering from the response
        if (result.extraction) setExtractResult(result.extraction);
        if (result.tampering) setTamperResult(result.tampering);
    };

    if (permissions !== 'admin') {
        return (
            <Box p={3}>
                <Typography variant="h6" color="error">
                    {t('fingerprintVerify.permissionDenied')}
                </Typography>
            </Box>
        );
    }

    const hasResults = verifyResult || extractResult || tamperResult || dbMatchResult;

    return (
        <Box>
            <Title title={t('fingerprintVerify.title')} />

            {/* ── Header ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <FingerprintIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={600}>
                                {t('fingerprintVerify.heading')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('fingerprintVerify.description')}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* ── File Upload ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                        {t('fingerprintVerify.steps.upload')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept=".pdf,.epub"
                            onChange={handleFileChange}
                        />
                        <JumpstartButton
                            label={file ? file.name : t('fingerprintVerify.upload.select')}
                            type="secondary"
                            startIcon={<CloudUploadIcon fontSize="small" />}
                            disabled={loading}
                            onClick={() => fileInputRef.current?.click()}
                        />
                        {file && (
                            <Tag type="light" color="info">
                                {`${documentType.toUpperCase()} · ${(file.size / 1024).toFixed(0)} KB`}
                            </Tag>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* ── Mode Selection ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                        {t('fingerprintVerify.steps.mode')}
                    </Typography>
                    <RadioGroup
                        value={mode}
                        onChange={(e) => { setMode(e.target.value as VerifyMode); clearResults(); }}
                    >
                        <FormControlLabel
                            value="manual"
                            control={<Radio />}
                            label={t('fingerprintVerify.mode.manual')}
                        />
                        <FormControlLabel
                            value="db_all"
                            control={<Radio />}
                            label={t('fingerprintVerify.mode.dbAll')}
                        />
                        <FormControlLabel
                            value="db_specific"
                            control={<Radio />}
                            label={t('fingerprintVerify.mode.dbSpecific')}
                        />
                    </RadioGroup>

                    <Divider sx={{ my: 2 }} />

                    {/* ── Manual fields ── */}
                    {mode === 'manual' && (
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <JumpstartFilterText
                                label={t('fingerprintVerify.manual.queueIdLabel')}
                                value={manualQueueId}
                                onChange={setManualQueueId}
                                disabled={loading}
                                minWidth={350}
                                placeholder={t('fingerprintVerify.manual.queueIdPlaceholder')}
                            />
                            <JumpstartFilterText
                                label={t('fingerprintVerify.manual.emailLabel')}
                                value={manualEmail}
                                onChange={setManualEmail}
                                disabled={loading}
                                minWidth={300}
                                placeholder={t('fingerprintVerify.manual.emailPlaceholder')}
                            />
                        </Box>
                    )}

                    {/* ── DB specific selector ── */}
                    {mode === 'db_specific' && (
                        <JumpstartFilterSelect
                            label={t('fingerprintVerify.dbSpecific.selectLabel')}
                            value={selectedFpId === '' ? '' : String(selectedFpId)}
                            onChange={(v) => setSelectedFpId(v === '' ? '' : Number(v))}
                            fullWidth
                            choices={[
                                {
                                    id: '',
                                    name: loadingFps
                                        ? t('fingerprintVerify.dbSpecific.loading')
                                        : dbFingerprints.length === 0
                                            ? t('fingerprintVerify.dbSpecific.empty')
                                            : t('fingerprintVerify.dbSpecific.placeholder'),
                                },
                                ...dbFingerprints.map((fp) => ({
                                    id: String(fp.id),
                                    name: `#${fp.id} · ${fp.consumer_email} · ${fp.sku} · ${fp.document_type.toUpperCase()} · ${formatDate(fp.embedded_at)}`,
                                })),
                            ]}
                        />
                    )}

                    {/* ── DB all info ── */}
                    {mode === 'db_all' && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            {t('fingerprintVerify.dbAll.info', { count: dbFingerprints.length || '…' })}
                            {loadingFps && <CircularProgress size={14} sx={{ ml: 1 }} />}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* ── Submit ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <ButtonField type="primary" size="large">
                        <button
                            type="button"
                            onClick={handleVerify}
                            disabled={loading || !file || (mode === 'db_specific' && selectedFpId === '')}
                        >
                            {loading ? t('fingerprintVerify.actions.submitting') : t('fingerprintVerify.actions.submit')}
                        </button>
                    </ButtonField>
                </CardContent>
            </Card>

            {/* ── Error ── */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* ── Results ── */}
            {hasResults && (
                <>
                    {/* Extraction result */}
                    {extractResult && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <FingerprintIcon color="info" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {t('fingerprintVerify.extraction.title')}
                                    </Typography>
                                </Box>

                                {extractResult.status === 'extracted' ? (
                                    <>
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            {t('fingerprintVerify.extraction.foundAlert')}
                                        </Alert>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600, width: 200 }}>{t('fingerprintVerify.extraction.fields.method')}</TableCell>
                                                        <TableCell>{extractResult.verification_method || '—'}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('fingerprintVerify.extraction.fields.queueId')}</TableCell>
                                                        <TableCell>
                                                            <code>{extractResult.queue_id || extractResult.payload_parts?.queue_id || '—'}</code>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t('fingerprintVerify.extraction.fields.email')}</TableCell>
                                                        <TableCell>
                                                            {extractResult.consumer_email || extractResult.payload_parts?.consumer_email || '—'}
                                                        </TableCell>
                                                    </TableRow>
                                                    {extractResult.payload_parts?.consumer_name && (
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>{t('fingerprintVerify.extraction.fields.name')}</TableCell>
                                                            <TableCell>
                                                                {extractResult.payload_parts.consumer_name} {extractResult.payload_parts.consumer_surname || ''}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    {extractResult.payload_parts?.sku && (
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>{t('fingerprintVerify.extraction.fields.sku')}</TableCell>
                                                            <TableCell>{extractResult.payload_parts.sku}</TableCell>
                                                        </TableRow>
                                                    )}
                                                    {extractResult.bits_extracted != null && (
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>{t('fingerprintVerify.extraction.fields.bitsExtracted')}</TableCell>
                                                            <TableCell>{extractResult.bits_extracted}</TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                ) : (
                                    <Alert severity="warning">
                                        {t('fingerprintVerify.extraction.notFoundAlert')}
                                        {extractResult.status === 'not_found' ? ` — ${(extractResult as any).reason || ''}` : ''}
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Verify result (manual mode) */}
                    {verifyResult && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <VerifiedUserIcon color={verifyResult.is_verified ? 'success' : 'error'} />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {t('fingerprintVerify.verifyResult.title')}
                                    </Typography>
                                </Box>

                                <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                                    <Chip
                                        label={verifyResult.is_verified ? t('fingerprintVerify.verifyResult.verified') : t('fingerprintVerify.verifyResult.notVerified')}
                                        icon={verifyResult.is_verified ? <CheckCircleIcon /> : <ErrorIcon />}
                                        color={verifyResult.is_verified ? 'success' : 'error'}
                                        variant="filled"
                                        sx={{ fontWeight: 700 }}
                                    />
                                    <Chip
                                        label={t('fingerprintVerify.verifyResult.confidence', { value: verifyResult.confidence ?? 0 })}
                                        icon={<SecurityIcon />}
                                        color={
                                            (verifyResult.confidence ?? 0) >= 80 ? 'success'
                                            : (verifyResult.confidence ?? 0) >= 50 ? 'warning'
                                            : 'error'
                                        }
                                        variant="outlined"
                                    />
                                    {verifyResult.meta_verified != null && (
                                        <Chip
                                            label={verifyResult.meta_verified ? t('fingerprintVerify.verifyResult.metaOk') : t('fingerprintVerify.verifyResult.metaFail')}
                                            color={verifyResult.meta_verified ? 'success' : 'default'}
                                            variant="outlined"
                                            size="small"
                                        />
                                    )}
                                    {verifyResult.content_verified != null && (
                                        <Chip
                                            label={verifyResult.content_verified ? t('fingerprintVerify.verifyResult.contentOk') : t('fingerprintVerify.verifyResult.contentFail')}
                                            color={verifyResult.content_verified ? 'success' : 'default'}
                                            variant="outlined"
                                            size="small"
                                        />
                                    )}
                                </Box>

                                {verifyResult.reason && (
                                    <Typography variant="body2" color="text.secondary">
                                        {verifyResult.reason}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Tampering result */}
                    {tamperResult && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    {tamperResult.tampering_detected
                                        ? <GppBadIcon color="error" />
                                        : <GppGoodIcon color="success" />
                                    }
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {t('fingerprintVerify.tamper.title')}
                                    </Typography>
                                </Box>

                                <Alert
                                    severity={tamperResult.tampering_detected ? 'error' : 'success'}
                                    sx={{ mb: 2 }}
                                >
                                    {tamperResult.tampering_detected
                                        ? t('fingerprintVerify.tamper.detected')
                                        : t('fingerprintVerify.tamper.notDetected')
                                    }
                                </Alert>

                                {tamperResult.indicators && tamperResult.indicators.length > 0 && (
                                    <Box display="flex" gap={1} flexWrap="wrap">
                                        {tamperResult.indicators.map((ind, i) => (
                                            <Chip
                                                key={i}
                                                label={ind}
                                                size="small"
                                                color={
                                                    ind.includes('present') || ind.includes('ok') || ind.includes('readable')
                                                        ? 'success'
                                                        : ind.includes('missing') || ind.includes('error')
                                                        ? 'warning'
                                                        : 'default'
                                                }
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* DB Match results */}
                    {dbMatchResult && (
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={2}>
                                    <SearchIcon color="primary" />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {t('fingerprintVerify.dbMatch.title')}
                                    </Typography>
                                </Box>

                                {dbMatchResult.status === 'no_fingerprint' ? (
                                    <Alert severity="warning">
                                        {t('fingerprintVerify.dbMatch.noFingerprint')}
                                    </Alert>
                                ) : dbMatchResult.matches_found === 0 ? (
                                    <Alert severity="info">
                                        {t('fingerprintVerify.dbMatch.noMatches')}
                                    </Alert>
                                ) : (
                                    <>
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            {t('fingerprintVerify.dbMatch.matchesFound', { count: dbMatchResult.matches_found })}
                                        </Alert>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.id')}</TableCell>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.email')}</TableCell>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.name')}</TableCell>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.sku')}</TableCell>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.type')}</TableCell>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.queueId')}</TableCell>
                                                        <TableCell>{t('fingerprintVerify.dbMatch.fields.match')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dbMatchResult.matches.map((m) => (
                                                        <TableRow key={m.fingerprint_id}>
                                                            <TableCell>{m.fingerprint_id}</TableCell>
                                                            <TableCell>{m.consumer_email}</TableCell>
                                                            <TableCell>{m.consumer_name} {m.consumer_surname}</TableCell>
                                                            <TableCell>{m.sku}</TableCell>
                                                            <TableCell>{m.document_type.toUpperCase()}</TableCell>
                                                            <TableCell>
                                                                <code style={{ fontSize: '0.75rem' }}>
                                                                    {m.queue_id.substring(0, 12)}…
                                                                </code>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box display="flex" gap={0.5}>
                                                                    <Chip
                                                                        label={m.queue_id_match ? t('fingerprintVerify.dbMatch.tags.qidOk') : t('fingerprintVerify.dbMatch.tags.qidFail')}
                                                                        size="small"
                                                                        color={m.queue_id_match ? 'success' : 'default'}
                                                                        variant="outlined"
                                                                    />
                                                                    <Chip
                                                                        label={m.email_match ? t('fingerprintVerify.dbMatch.tags.emailOk') : t('fingerprintVerify.dbMatch.tags.emailFail')}
                                                                        size="small"
                                                                        color={m.email_match ? 'success' : 'default'}
                                                                        variant="outlined"
                                                                    />
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </Box>
    );
};
